/**
 * MotherGeneralTributesAdmin — the wall, as the ministry manages it.
 *
 * Every tribute already on record is listed here, oldest first, including the
 * historical ones imported from the memorial site. Editing one changes that
 * same record: there is no second copy of the wall anywhere, so nothing a
 * mourner wrote in 2016 can be lost by adding one today.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Pencil, Plus, Trash2, Flame, ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const EMPTY = { title: '', name: '', assembly: '', image_url: '', message: '', date: '' };

const TributeForm = ({ value, onChange }) => (
  <div className="space-y-3">
    <div>
      <Label htmlFor="mg-title">Title <span className="text-xs text-slate-400">(optional)</span></Label>
      <Input id="mg-title" value={value.title} data-testid="mg-admin-title"
             placeholder="A mother to thousands"
             onChange={(e) => onChange({ ...value, title: e.target.value })} />
    </div>
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <Label htmlFor="mg-name">Contributor *</Label>
        <Input id="mg-name" value={value.name} data-testid="mg-admin-name"
               placeholder="Pastor J. Moyo"
               onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="mg-assembly">Assembly <span className="text-xs text-slate-400">(optional)</span></Label>
        <Input id="mg-assembly" value={value.assembly} data-testid="mg-admin-assembly"
               placeholder="Now Faith Newport"
               onChange={(e) => onChange({ ...value, assembly: e.target.value })} />
      </div>
    </div>
    <div>
      <Label htmlFor="mg-message">Tribute *</Label>
      <Textarea id="mg-message" rows={6} value={value.message} data-testid="mg-admin-message"
                placeholder="What you would like remembered…"
                onChange={(e) => onChange({ ...value, message: e.target.value })} />
    </div>
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <Label htmlFor="mg-image">Photograph link <span className="text-xs text-slate-400">(optional)</span></Label>
        <Input id="mg-image" value={value.image_url} data-testid="mg-admin-image"
               placeholder="https://…/photo.jpg"
               onChange={(e) => onChange({ ...value, image_url: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="mg-date">Posted on <span className="text-xs text-slate-400">(optional)</span></Label>
        <Input id="mg-date" value={value.date} data-testid="mg-admin-date"
               placeholder="2026-06-14"
               onChange={(e) => onChange({ ...value, date: e.target.value })} />
      </div>
    </div>
  </div>
);

const MotherGeneralTributesAdmin = () => {
  const qc = useQueryClient();
  const [limit, setLimit] = useState(300);
  const [term, setTerm] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mg-tributes-manage', limit],
    queryFn: () => api.get(`/tributes/mother-general/manage?limit=${limit}`).then((r) => r.data),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['mg-tributes-manage'] });

  const create = useMutation({
    mutationFn: (body) => api.post('/tributes/mother-general', body),
    onSuccess: () => {
      toast.success('Tribute posted');
      setAdding(false);
      setDraft(EMPTY);
      refresh();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not post that tribute'),
  });

  const update = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/tributes/mother-general/${id}`, body),
    onSuccess: () => {
      toast.success('Tribute updated');
      setEditing(null);
      refresh();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not update that tribute'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/tributes/mother-general/${id}`),
    onSuccess: () => {
      toast.success('Tribute removed');
      refresh();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not remove that tribute'),
  });

  const all = data?.items || [];
  const needle = term.trim().toLowerCase();
  const items = needle
    ? all.filter((t) => `${t.name} ${t.assembly} ${t.title} ${t.message}`.toLowerCase().includes(needle))
    : all;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6" data-testid="mg-tributes-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/tributes" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:underline"
                data-testid="mg-admin-back">
            <ArrowLeft className="h-3 w-3" /> All tribute walls
          </Link>
          <h1 className="mt-1 flex items-center gap-2 font-serif text-2xl text-slate-900">
            <Flame className="h-5 w-5 text-amber-600" /> Mother General Tributes
          </h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="mg-admin-summary">
            {data?.total ?? 0} on the wall
            {data?.pending ? ` · ${data.pending} awaiting approval` : ''} · shown on every assembly page
          </p>
        </div>
        <Button onClick={() => { setDraft(EMPTY); setAdding(true); }} data-testid="mg-admin-add-btn">
          <Plus className="mr-1 h-4 w-4" /> Add a tribute
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search contributor, assembly or words…" value={term}
               className="pl-10" data-testid="mg-admin-search"
               onChange={(e) => setTerm(e.target.value)} />
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-slate-500" data-testid="mg-admin-loading">
          <Loader2 className="mr-2 inline h-5 w-5 animate-spin" /> Loading the wall…
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Oldest first, as they were written
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100 p-0">
            {items.length === 0 ? (
              <p className="p-8 text-center text-sm text-slate-500" data-testid="mg-admin-empty">
                Nothing matches that search.
              </p>
            ) : items.map((t) => (
              <div key={t.id} className="flex flex-wrap items-start gap-3 p-4"
                   data-testid={`mg-admin-row-${t.id}`}>
                {t.image_url ? (
                  <img src={t.image_url} alt={t.name} className="h-14 w-14 rounded object-cover"
                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{t.name}</span>
                    {t.assembly && <Badge variant="outline" className="text-[10px]">{t.assembly}</Badge>}
                    {t.status === 'pending' && (
                      <Badge className="bg-amber-100 text-[10px] text-amber-900">awaiting approval</Badge>
                    )}
                    {t.status === 'hidden' && (
                      <Badge variant="outline" className="text-[10px]">hidden</Badge>
                    )}
                    {t.imported && <Badge variant="outline" className="bg-slate-50 text-[10px]">from the memorial site</Badge>}
                    <span className="text-[11px] text-slate-400">{t.date}</span>
                  </div>
                  {t.title && <p className="mt-0.5 text-sm text-slate-700">{t.title}</p>}
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{t.message}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  {t.status !== 'approved' && (
                    <Button size="sm" variant="outline" className="h-8 text-xs"
                            disabled={update.isPending}
                            onClick={() => update.mutate({ id: t.id, body: { status: 'approved' } })}
                            data-testid={`mg-admin-approve-${t.id}`}>
                      Approve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                          onClick={() => setEditing({ ...EMPTY, ...t })}
                          data-testid={`mg-admin-edit-${t.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Remove the tribute from ${t.name}? This cannot be undone.`)) {
                              remove.mutate(t.id);
                            }
                          }}
                          data-testid={`mg-admin-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {!isLoading && all.length < (data?.total || 0) && (
        <div className="text-center">
          <Button variant="outline" onClick={() => setLimit((l) => l + 300)}
                  data-testid="mg-admin-load-more">
            Show more ({data.total - all.length} remaining)
          </Button>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg" data-testid="mg-admin-add-dialog">
          <DialogHeader>
            <DialogTitle>Add a tribute</DialogTitle>
            <DialogDescription>
              It appears on every assembly page as soon as you post it.
            </DialogDescription>
          </DialogHeader>
          <TributeForm value={draft} onChange={setDraft} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button disabled={create.isPending || !draft.name.trim() || draft.message.trim().length < 3}
                    onClick={() => create.mutate(draft)}
                    data-testid="mg-admin-add-save">
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post tribute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="mg-admin-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit tribute</DialogTitle>
            <DialogDescription>
              Only the fields you change are written back.
            </DialogDescription>
          </DialogHeader>
          {editing && <TributeForm value={editing} onChange={setEditing} />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={update.isPending}
                    onClick={() => update.mutate({
                      id: editing.id,
                      body: {
                        title: editing.title, name: editing.name,
                        assembly: editing.assembly, image_url: editing.image_url,
                        message: editing.message, date: editing.date,
                      },
                    })}
                    data-testid="mg-admin-edit-save">
              {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MotherGeneralTributesAdmin;
